/**
 * Pictures the SE puts in the report.
 *
 * A workshop produces things a chart cannot say: a photograph of the whiteboard,
 * a screenshot from the customer's own console, a diagram their architect drew.
 *
 * These travel with the workshop rather than with the laptop. An uploaded deck
 * is kept locally because it can run to a hundred megabytes and only the person
 * presenting needs it; a report picture is part of the deliverable, so a
 * colleague opening the same workshop has to see it and export it. That is only
 * safe because everything here is shrunk first: the workshop state is PATCHed to
 * the backend as one JSON document, so what goes into it has to stay small.
 *
 * The bytes live under `WorkshopState.reportImages`, keyed by image id, and
 * deliberately NOT inside `reportLayout`. Layout changes on every chart note and
 * section move, and re-sending every picture each time is the thing that would
 * make this a bad idea.
 */

/** The file the SE picks. Anything bigger is a mistake, not a report picture. */
export const MAX_IMAGE_BYTES = 12 * 1024 * 1024;

/** Longest edge after shrinking. Wider than the text column of either export. */
const MAX_EDGE = 1600;

/**
 * What one picture may weigh once shrunk.
 *
 * The whole workshop is one JSON document, so this is a budget rather than a
 * technical limit. At 1600px it is not normally close.
 */
const MAX_STORED_BYTES = 2 * 1024 * 1024;

export const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';

/**
 * What a browser will draw and what both exports can embed.
 *
 * SVG is deliberately absent. An SVG is a document that can carry script, it
 * arrives here from a customer's machine, and neither Word nor the PDF writer
 * takes one anyway.
 */
const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

export interface PreparedImage {
  dataUrl: string;
  width: number;
  height: number;
}

function load(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as a picture.'));
    };
    img.src = url;
  });
}

/** Roughly how many bytes a data URL carries, without decoding it. */
function weigh(dataUrl: string): number {
  const at = dataUrl.indexOf(',');
  return at === -1 ? dataUrl.length : Math.floor(((dataUrl.length - at - 1) * 3) / 4);
}

/**
 * Shrinks a picture and encodes it for storage.
 *
 * Both PNG and JPEG are produced and the smaller one wins. That gets the right
 * answer without guessing at the content: a screenshot of flat interface colours
 * compresses far better as PNG, a photograph far better as JPEG, and neither
 * needs a heuristic about which one this is.
 */
export async function prepareReportImage(file: File): Promise<PreparedImage> {
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error('Pictures have to be PNG, JPEG, GIF or WebP.');
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error(
      `That picture is ${Math.round(file.size / 1024 / 1024)} MB. The limit is ${MAX_IMAGE_BYTES / 1024 / 1024} MB.`,
    );
  }

  const img = await load(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser could not redraw the picture.');
  // JPEG has no transparency, so anything transparent has to land on something.
  // White matches both the page on screen and the paper it prints on.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const png = canvas.toDataURL('image/png');
  const jpeg = canvas.toDataURL('image/jpeg', 0.85);
  const dataUrl = weigh(jpeg) < weigh(png) ? jpeg : png;

  if (weigh(dataUrl) > MAX_STORED_BYTES) {
    throw new Error('That picture is too detailed to store with the workshop. Try a smaller one.');
  }

  return { dataUrl, width, height };
}

/** The file extension a stored picture should be written under in a .docx. */
export function imageExtension(dataUrl: string): 'png' | 'jpeg' {
  return dataUrl.startsWith('data:image/jpeg') ? 'jpeg' : 'png';
}
